import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faEllipsisH,
  faAngleDoubleLeft,
  faAngleDoubleRight,
} from '@fortawesome/free-solid-svg-icons';
import '../StylesComponents/pagination.css';

/**
 * Pagination
 *
 * Desktop — smart ellipsis navigation:
 *   «  Prev   1 … 23 24 [25] 26 27 … 248   Next  »
 *
 * Mobile — clean compact single-row controls only:
 *   «  ‹  25 / 248  ›  »
 *   (no Load More button — navigation scrolls to top automatically)
 *
 * Props
 *   currentPage  {number}   1-indexed current page
 *   totalPages   {number}   total navigable pages
 *   onPageChange {Function} called with new page number
 *   onPrefetch   {Function} optional — fires with page number on Next hover/focus
 *   isLoading    {boolean}  optional — dims the bar during a page transition
 */
function Pagination({ currentPage, totalPages, onPageChange, onPrefetch, isLoading = false }) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    onPageChange(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrefetch = useCallback(
    (page) => {
      if (onPrefetch && page >= 1 && page <= totalPages && page !== currentPage) {
        onPrefetch(page);
      }
    },
    [onPrefetch, currentPage, totalPages]
  );

  if (!totalPages || totalPages <= 1) return null;

  // ── Page number computation ─────────────────────────────────────────────
  // Always shows: first page, last page, current ±2, and smart ellipsis.
  const getPageNumbers = () => {
    const pages = [];
    const WINDOW = isMobile ? 1 : 2;

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(1);

    const start = Math.max(2, currentPage - WINDOW);
    const end   = Math.min(totalPages - 1, currentPage + WINDOW);

    if (start > 2) pages.push('ellipsis-start');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push('ellipsis-end');

    pages.push(totalPages);
    return pages;
  };

  // ── Mobile compact layout ───────────────────────────────────────────────
  if (isMobile) {
    return (
      <nav
        className={`stockroom-pagination${isLoading ? ' pag-loading' : ''}`}
        aria-label="Product Pagination"
      >
        <div className="pagination-mobile-compact">
          <button
            className="pag-nav-btn first"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(1)}
            aria-label="First Page"
          >
            <FontAwesomeIcon icon={faAngleDoubleLeft} />
          </button>

          <button
            className="pag-nav-btn prev"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            onMouseEnter={() => handlePrefetch(currentPage - 1)}
            aria-label="Previous Page"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>

          <div className="pag-mobile-info" aria-live="polite" aria-atomic="true">
            <span className="pag-mobile-current">{currentPage}</span>
            <span className="pag-mobile-sep">/</span>
            <span className="pag-mobile-total">{totalPages}</span>
          </div>

          <button
            className="pag-nav-btn next"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
            onMouseEnter={() => handlePrefetch(currentPage + 1)}
            onFocus={() => handlePrefetch(currentPage + 1)}
            aria-label="Next Page"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>

          <button
            className="pag-nav-btn last"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(totalPages)}
            aria-label="Last Page"
          >
            <FontAwesomeIcon icon={faAngleDoubleRight} />
          </button>
        </div>
      </nav>
    );
  }

  // ── Desktop full layout ─────────────────────────────────────────────────
  return (
    <nav
      className={`stockroom-pagination${isLoading ? ' pag-loading' : ''}`}
      aria-label="Product Pagination"
    >
      <div className="pagination-core-controls">
        {/* Left: first + prev */}
        <div className="pagination-group">
          <button
            className="pag-nav-btn first"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(1)}
            aria-label="First Page"
          >
            <FontAwesomeIcon icon={faAngleDoubleLeft} />
          </button>

          <button
            className="pag-nav-btn prev"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            onMouseEnter={() => handlePrefetch(currentPage - 1)}
            aria-label="Previous Page"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            <span className="nav-text">PREV</span>
          </button>
        </div>

        {/* Centre: page numbers */}
        <div className="pag-nodes-container">
          {getPageNumbers().map((page, idx) => {
            if (page === 'ellipsis-start' || page === 'ellipsis-end') {
              return (
                <div key={`sep-${idx}`} className="pag-node-separator" aria-hidden="true">
                  <FontAwesomeIcon icon={faEllipsisH} />
                </div>
              );
            }
            return (
              <button
                key={page}
                className={`pag-node${currentPage === page ? ' active' : ''}`}
                onClick={() => handlePageChange(page)}
                onMouseEnter={() => handlePrefetch(page)}
                aria-current={currentPage === page ? 'page' : undefined}
                aria-label={`Page ${page}`}
              >
                <span className="node-num">{page}</span>
              </button>
            );
          })}
        </div>

        {/* Right: next + last */}
        <div className="pagination-group">
          <button
            className="pag-nav-btn next"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
            onMouseEnter={() => handlePrefetch(currentPage + 1)}
            onFocus={() => handlePrefetch(currentPage + 1)}
            aria-label="Next Page"
          >
            <span className="nav-text">NEXT</span>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>

          <button
            className="pag-nav-btn last"
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(totalPages)}
            aria-label="Last Page"
          >
            <FontAwesomeIcon icon={faAngleDoubleRight} />
          </button>
        </div>
      </div>

      {/* Page info line — helps users orient on large catalogues */}
      <div className="pag-info-line" aria-live="polite" aria-atomic="true">
        PAGE <strong>{currentPage}</strong> / {totalPages}
      </div>
    </nav>
  );
}

export default Pagination;
